import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Car, Clock, MapPin, Plane, TrainFront } from "lucide-react";
import { WEDDING_DATE_ISO, VENUE_COORDS, ORIGIN_CITIES } from "@/lib/wedding-data";

const toRad = (d) => (d * Math.PI) / 180;
const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
};

export default function Journey() {
  const [status, setStatus] = useState("idle");
  const [distance, setDistance] = useState(null);

  useEffect(() => {
    const applySaved = () => {
      const saved = localStorage.getItem("viyush-distance");
      if (saved != null && saved !== "") {
        setDistance(Number(saved));
        setStatus("done");
      }
    };
    applySaved();
    window.addEventListener("viyush-location", applySaved);
    return () => window.removeEventListener("viyush-location", applySaved);
  }, []);

  const weddingDate = useMemo(() => new Date(WEDDING_DATE_ISO), []);
  const isIst =
    Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Kolkata";
  const localTime = weddingDate.toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

  const locate = () => {
    if (!navigator.geolocation) {
      setStatus("unavailable");
      return;
    }
    setStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const d = haversineKm(
          pos.coords.latitude,
          pos.coords.longitude,
          VENUE_COORDS.lat,
          VENUE_COORDS.lng
        );
        setDistance(d);
        setStatus("done");
        localStorage.setItem("viyush-distance", String(d));
      },
      (err) => setStatus(err && err.code === 1 ? "blocked" : "unavailable"),
      { timeout: 15000, maximumAge: 60000 }
    );
  };

  const pickCity = (e) => {
    const city = ORIGIN_CITIES.find((c) => c.name === e.target.value);
    if (!city) return;
    const d = haversineKm(city.lat, city.lng, VENUE_COORDS.lat, VENUE_COORDS.lng);
    setDistance(d);
    setStatus("done");
    localStorage.setItem("viyush-distance", String(d));
  };

  const advice = useMemo(() => {
    if (distance == null) return null;
    if (distance >= 500)
      return {
        Icon: Plane,
        title: "Book a flight or train",
        text: `You're roughly ${distance.toLocaleString()} km away. Fly into Delhi (DEL) - about 50 minutes from the venue - or take a train to New Delhi or Gurgaon. Late-January seats fill fast, so book early.`,
        links: true,
      };
    if (distance >= 100)
      return {
        Icon: TrainFront,
        title: "A road trip or short train ride",
        text: `You're roughly ${distance.toLocaleString()} km away - a comfortable drive or an easy train journey. Cabs from Gurgaon's stations reach the resort in under an hour.`,
      };
    return {
      Icon: Car,
      title: "You're practically next door",
      text: `Only about ${distance.toLocaleString()} km away - a cab or a leisurely drive brings you straight to the celebration.`,
    };
  }, [distance]);

  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="scroll-mt-24 py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl border border-[#D4AF37]/30 bg-white/60 p-8 md:p-10"
        >
          <p className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-6">
            <Clock className="h-4 w-4" /> In Your Time Zone
          </p>
          <p className="font-serif text-2xl md:text-3xl tracking-tight text-[#1A1A1A]">
            Sunday, January 24, 2027 · 5:00 PM IST
          </p>
          <p
            className="mt-3 text-sm text-[#1A1A1A]/65"
            data-testid="journey-local-time"
          >
            {isIst
              ? "You're on Indian Standard Time - the same clock as the celebration."
              : `For you, that's ${localTime}. The countdown above already runs on your clock.`}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-2xl border border-[#D4AF37]/30 bg-white/60 p-8 md:p-10"
        >
          <p className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-6">
            <MapPin className="h-4 w-4" /> Travelling In?
          </p>

          {status === "done" && advice ? (
            <div data-testid="journey-distance-result">
              <p className="font-serif text-2xl md:text-3xl tracking-tight text-[#1A1A1A] flex items-center gap-3">
                <advice.Icon className="h-7 w-7 text-[#D4AF37]" />
                {advice.title}
              </p>
              <p className="mt-3 text-sm text-[#1A1A1A]/65 leading-relaxed">
                {advice.text}
              </p>
              {advice.links && (
                <div className="mt-5 flex flex-wrap gap-6">
                  <a
                    href="https://www.google.com/travel/flights?q=flights%20to%20New%20Delhi"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="journey-flights-link"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5D171E] border-b border-[#D4AF37] pb-1 hover:text-[#471016] transition-colors duration-300"
                  >
                    Flights to Delhi <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href="https://www.irctc.co.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="journey-trains-link"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5D171E] border-b border-[#D4AF37] pb-1 hover:text-[#471016] transition-colors duration-300"
                  >
                    Trains on IRCTC <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="font-serif text-2xl md:text-3xl tracking-tight text-[#1A1A1A]">
                How far is the journey?
              </p>
              <p className="mt-3 text-sm text-[#1A1A1A]/65 leading-relaxed">
                Share your location and we'll suggest whether to drive, take the
                train, or book a flight.
              </p>
              <button
                onClick={locate}
                disabled={status === "locating"}
                data-testid="journey-locate-button"
                className="mt-6 rounded-full bg-[#5D171E] text-[#FDFBF7] text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-[#471016] transition-colors duration-300 disabled:opacity-60"
              >
                {status === "locating"
                  ? "Locating..."
                  : status === "blocked"
                    ? "Try Again"
                    : "Use My Location"}
              </button>
              {status === "blocked" && (
                <p
                  className="mt-4 text-sm text-[#1A1A1A]/60"
                  data-testid="journey-blocked-note"
                >
                  Location access is blocked for this site. Tap the lock icon in
                  your browser's address bar, allow Location, then try again -
                  or simply pick your city below.
                </p>
              )}
              {status === "unavailable" && (
                <p
                  className="mt-4 text-sm text-[#1A1A1A]/60"
                  data-testid="journey-unavailable-note"
                >
                  We couldn't pinpoint you automatically - pick your city below
                  and we'll take it from there.
                </p>
              )}
              <div className="mt-6">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#5D171E]/70">
                  Or tell us your city
                </label>
                <select
                  defaultValue=""
                  onChange={pickCity}
                  data-testid="journey-city-select"
                  className="mt-2 w-full rounded-md border border-[#D4AF37]/40 bg-white/70 px-3 py-2.5 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#5D171E]"
                >
                  <option value="" disabled>
                    Choose your starting city...
                  </option>
                  {ORIGIN_CITIES.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="mt-4 text-xs text-[#1A1A1A]/50">
                Rule of thumb: over 500 km away, book a flight to Delhi or a
                train early; closer than that, a drive or cab is easiest. The
                venue is ~50 min from IGI Airport.
              </p>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}
